"use client";

import { useEffect, type MutableRefObject } from "react";
import {
  MEDIA_STOP_PLAYBACK_EVENT,
  type MediaStopPlaybackEventDetail,
} from "@/lib/mediaPlayback";
import { loadSpotifySdk } from "./spotifyWebPlayer.runtime";
import { clampWebPlayerVolume } from "./webPlayerShared";
import {
  disconnectSpotifyPlayer,
  spotifySharedStore,
  toSpotifyTrackMeta,
  type SpotifyPlayerLike,
  type SpotifySharedState,
} from "./spotifyWebPlayer.shared";

let pollingInterval: number | null = null;
let pollingSubscribers = 0;

type PatchSharedState = (patch: Partial<SpotifySharedState>) => void;

type UseSpotifyPlayerEffectsParams = {
  spotifyEnabled: boolean;
  isReady: boolean;
  deviceId: string | null;
  autoTransfer: boolean;
  autoTransferPlay: boolean;
  playerInitTick: number;
  playerRef: MutableRefObject<SpotifyPlayerLike | null>;
  patchSharedState: PatchSharedState;
  syncStateFromShared: () => void;
  getPlayerOrReset: (reason: string) => SpotifyPlayerLike | null;
  initPlayer: () => void;
  stopPlayback: (resetPosition?: boolean) => Promise<void>;
  resumePlaybackHere: (play?: boolean) => Promise<boolean>;
};

export function useSpotifyPlayerEffects({
  spotifyEnabled,
  isReady,
  deviceId,
  autoTransfer,
  autoTransferPlay,
  playerInitTick,
  playerRef,
  patchSharedState,
  syncStateFromShared,
  getPlayerOrReset,
  initPlayer,
  stopPlayback,
  resumePlaybackHere,
}: UseSpotifyPlayerEffectsParams) {
  useEffect(() => {
    const unsubscribe = spotifySharedStore.subscribe(syncStateFromShared);
    syncStateFromShared();
    return unsubscribe;
  }, [syncStateFromShared]);

  useEffect(() => {
    if (spotifyEnabled) return;
    disconnectSpotifyPlayer(spotifySharedStore.getState().player);
    playerRef.current = null;
    patchSharedState({
      player: null,
      deviceId: null,
      isReady: false,
      isPlaying: false,
      currentTrack: null,
      positionMs: 0,
      durationMs: 0,
      needsTransfer: false,
      error: null,
      hasPlaybackState: false,
    });
  }, [patchSharedState, playerRef, spotifyEnabled]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!spotifyEnabled) return;
    return loadSpotifySdk(initPlayer);
  }, [initPlayer, playerInitTick, spotifyEnabled]);

  useEffect(() => {
    if (!spotifyEnabled) return;
    if (!isReady) return;
    if (!playerRef.current && !spotifySharedStore.getState().player) return;

    pollingSubscribers += 1;

    if (!pollingInterval) {
      pollingInterval = window.setInterval(async () => {
        const player = getPlayerOrReset("polling invalid Spotify player");
        if (!player) return;

        try {
          const state = await player.getCurrentState();
          if (!state) {
            const sharedState = spotifySharedStore.getState();
            if (sharedState.hasPlaybackState) {
              patchSharedState({
                needsTransfer: true,
              });
            }
            return;
          }

          const {
            position,
            duration,
            paused,
            track_window: { current_track },
          } = state;
          const sharedState = spotifySharedStore.getState();
          const nextTrack = toSpotifyTrackMeta(current_track);

          if (
            paused &&
            duration > 0 &&
            position >= duration - 500 &&
            (!state.track_window?.next_tracks ||
              state.track_window.next_tracks.length === 0)
          ) {
            patchSharedState({
              isPlaying: false,
              positionMs: duration,
              durationMs: duration,
              needsTransfer: false,
              hasPlaybackState: true,
              error: null,
            });
            return;
          }

          patchSharedState({
            isPlaying: !paused,
            positionMs: position,
            durationMs: duration,
            needsTransfer: false,
            hasPlaybackState: true,
            error: null,
            currentTrack:
              nextTrack && sharedState.currentTrack?.id !== nextTrack.id
                ? nextTrack
                : sharedState.currentTrack,
          });
        } catch {
          // Transient failures are ignored; polling will retry.
        }
      }, 1000);
    }

    return () => {
      pollingSubscribers -= 1;

      if (pollingSubscribers <= 0 && pollingInterval) {
        clearInterval(pollingInterval);
        pollingInterval = null;
        pollingSubscribers = 0;
      }
    };
  }, [getPlayerOrReset, isReady, patchSharedState, playerRef, spotifyEnabled]);

  useEffect(() => {
    if (!spotifyEnabled) return;
    if (!isReady) return;
    const player = getPlayerOrReset("volume sync invalid Spotify player");
    if (!player || typeof player.getVolume !== "function") return;
    let isMounted = true;

    player
      .getVolume()
      .then((value: number) => {
        if (!isMounted || typeof value !== "number") return;
        patchSharedState({
          volume: clampWebPlayerVolume(value),
        });
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [getPlayerOrReset, isReady, patchSharedState, spotifyEnabled]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!spotifyEnabled) return;

    const handleExternalStop = (event: Event) => {
      const detail =
        (event as CustomEvent<MediaStopPlaybackEventDetail>).detail ?? {};
      const stopPromise = stopPlayback(detail.resetPosition ?? true).catch(
        (error) => {
          console.warn("[SpotifyWebPlayer] stopPlayback failed", {
            reason: detail.reason ?? null,
            error: error instanceof Error ? error.message : String(error),
          });
        }
      );

      if (Array.isArray(detail.promises)) {
        detail.promises.push(stopPromise);
      }
    };

    window.addEventListener(
      MEDIA_STOP_PLAYBACK_EVENT,
      handleExternalStop as EventListener
    );
    return () => {
      window.removeEventListener(
        MEDIA_STOP_PLAYBACK_EVENT,
        handleExternalStop as EventListener
      );
    };
  }, [spotifyEnabled, stopPlayback]);

  useEffect(() => {
    if (!spotifyEnabled) return;
    if (!autoTransfer) return;
    if (!deviceId || !isReady) return;

    let cancelled = false;

    const transfer = async () => {
      await new Promise((resolve) => window.setTimeout(resolve, 250));
      if (cancelled) return;
      await resumePlaybackHere(autoTransferPlay);
    };

    void transfer();

    return () => {
      cancelled = true;
    };
  }, [
    autoTransfer,
    autoTransferPlay,
    deviceId,
    isReady,
    resumePlaybackHere,
    spotifyEnabled,
  ]);
}
